import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollectPayment } from '../../hooks/useCollectPayment';
import { TranslationKey } from '../../i18n/translationKeys';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useToastStore } from '../../stores/toastStore';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import { Button } from '../../ui/Button/Button';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { ModalShell } from '../../ui/ModalShell/ModalShell';
import { TextArea } from '../../ui/TextArea/TextArea';
import { IgnoreRelanceModal } from './IgnoreRelanceModal';

interface RelanceActionModalProps {
    /** A snoozed relance is brought forward, an ignored one reactivated. */
    entry: RelanceAgendaEntry;
    canCollectPayment: boolean;
    onClose: () => void;
}

export const RelanceActionModal: React.FC<RelanceActionModalProps> = ({ entry, canCollectPayment, onClose }) => {
    const { t } = useTranslation();
    const { item, status, note } = entry;
    const deactivateTask = useRelanceDeactivationStore(s => s.deactivateTask);
    const reactivateTask = useRelanceDeactivationStore(s => s.reactivateTask);
    const showToast = useToastStore(s => s.showToast);
    const collectPayment = useCollectPayment();
    const [reason, setReason] = useState('');
    const [snoozing, setSnoozing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const snoozed = status === 'snoozed';

    // A failed request leaves the modal open so what the user typed is not lost.
    const closeOnSuccess = async (request: Promise<boolean>) => {
        setSubmitting(true);
        const done = await request;
        setSubmitting(false);
        if (done) onClose();
        else showToast('error', t(TranslationKey.ApiErrors.Generic));
    };

    if (snoozing) {
        return (
            <IgnoreRelanceModal
                taskId={item.id}
                customerName={item.title}
                formattedAmount={item.amount}
                initialNote={note}
                onCancel={onClose}
                onConfirm={submission => closeOnSuccess(deactivateTask(item.id, submission))}
            />
        );
    }

    const handleCollect = () => {
        onClose();
        if (!collectPayment(item)) showToast('error', t(TranslationKey.Home.PaidAction.UnreachableError));
    };

    const handleConfirm = () => closeOnSuccess(reactivateTask(item.id, reason.trim() || null));

    return (
        <ModalShell
            heading={t(snoozed ? TranslationKey.Home.ActionModal.HeadingSnoozed : TranslationKey.Home.ActionModal.HeadingIgnored)}
            onClose={onClose}
            dismissAriaLabel={t(TranslationKey.Home.ActionModal.Close)}
            rootTestId={`relance-action-modal-${item.id}`}
            footer={
                <>
                    <Button variant="secondary" type="button" onClick={onClose} data-testid={`relance-action-cancel-${item.id}`}>
                        {t(TranslationKey.Home.ReactivateModal.Cancel)}
                    </Button>
                    <Button type="button" disabled={submitting} onClick={() => void handleConfirm()} data-testid={`relance-action-confirm-${item.id}`}>
                        {t(snoozed ? TranslationKey.Home.ActionModal.BringForward : TranslationKey.Home.ReactivateModal.Reactivate)}
                    </Button>
                </>
            }
        >
            <p data-testid={`relance-action-customer-${item.id}`} className="-mt-m text-s text-content-secondary">
                {item.title}{item.amount ? ` — ${item.amount}` : ''}
            </p>
            {note?.trim() && (
                <span data-testid={`relance-action-note-${item.id}`} className="self-start rounded-sm bg-surface-note-highlight px-xs text-s text-content">
                    {note}
                </span>
            )}
            <TextArea
                id={`relance-action-reason-${item.id}`}
                label={t(TranslationKey.Home.ReactivateModal.ReasonLabel)}
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={3}
                data-testid={`relance-action-reason-${item.id}`}
            />
            <div className="flex flex-col items-start gap-s">
                {canCollectPayment && (
                    <LinkButton variant="navigation" weight="medium" chevron="end" onClick={handleCollect} data-testid={`relance-action-collect-${item.id}`}>
                        {t(TranslationKey.Home.ActionModal.CollectBalance)}
                    </LinkButton>
                )}
                {snoozed && (
                    <LinkButton variant="navigation" weight="medium" chevron="end" onClick={() => setSnoozing(true)} data-testid={`relance-action-snooze-${item.id}`}>
                        {t(TranslationKey.Home.ActionModal.SnoozeOrIgnore)}
                    </LinkButton>
                )}
            </div>
        </ModalShell>
    );
};
